import { React, useRef, useEffect, useState } from 'react';

const generateSrcSet = (imageUrl, format, imageSize) => {
  const densities = [0.5, 0.75, 1, 1.5, 2];
  const returnedSrcSets: string[] = [];
  densities.forEach(dpr => {
    returnedSrcSets.push(`${imageUrl?.replace(/&width=\d+/g, '')}&width=${Math.ceil(imageSize * dpr)}&format=${format} ${dpr}x`);
  });
  return returnedSrcSets.join(', ');
};

export const Image = ({
  imageUrl,
  isPriority = false,
  responsiveImages = {},
  alt = '',
  isLazy = undefined,
  classes = '',
  width,
  height,
  fit = 'cover',
  ratio = 'auto',
  overlay = false,
  overlayOpacity = 0,
  borderRadius = 0,
  isOverlay = false,
  zIndex = 0
}) => {
  const imgRef = useRef(null);
  const [isAboveTheFold, setIsAboveTheFold] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  useEffect(() => {
    // Function to check if element is in viewport (above the fold)
    const checkIfAboveTheFold = () => {
      if (!imgRef.current) return;

      const viewportHeight = window.innerHeight;
      const rect = imgRef.current.getBoundingClientRect();

      const isVisible = rect.top < viewportHeight;

      setIsAboveTheFold(isVisible);
    };

    // Function to handle window resize
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      // Re-check above the fold when window resizes
      checkIfAboveTheFold();
    };

    checkIfAboveTheFold();

    // Add resize event listener
    // window.addEventListener('resize', handleResize);

    return () => {
      // window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine loading strategy - use manual override (isLazy) if provided, otherwise use position detection
  const loadingStrategy = isLazy === undefined ?
      (isAboveTheFold ? 'eager' : 'lazy') :
      (isLazy ? 'lazy' : 'eager');

  // Create aspect ratio class based on the ratio prop
  let aspectRatioClass = '';
  if (ratio && ratio !== 'auto') {
    // Handle common aspect ratios
    switch (ratio) {
      case '1:1':
        aspectRatioClass = 'aspect-square';
        break;
      case '4:3':
        aspectRatioClass = 'aspect-[4/3]';
        break;
      case '16:9':
        aspectRatioClass = 'aspect-[16/9]';
        break;
      case '2:3':
        aspectRatioClass = 'aspect-[2/3]';
        break;
      case '3:2':
        aspectRatioClass = 'aspect-[3/2]';
        break;
      default:
        // For custom ratios like "5:4", split and create a custom aspect class
        const [w, h] = ratio.split(':');
        aspectRatioClass = `aspect-[${w}/${h}]`;
    }
  }

  // Handle object-fit style
  let objectFitClass = '';
  switch (fit) {
    case 'cover':
      objectFitClass = 'object-cover';
      break;
    case 'contain':
      objectFitClass = 'object-contain';
      break;
    case 'fill':
      objectFitClass = 'object-fill';
      break;
    case 'none':
      objectFitClass = 'object-none';
      break;
    case 'scale-down':
      objectFitClass = 'object-scale-down';
      break;
    default:
      objectFitClass = 'object-cover';
  }

  // Combine object-fit with any existing classes
  const combinedClasses = `${classes} ${objectFitClass} ${aspectRatioClass}`.trim();

  // Create style object for border-radius
  const style: any = {};
  if (borderRadius > 0) {
    style.borderRadius = `${borderRadius}px`;
  }

  // Add positioning styles if this is an overlay
  if (isOverlay) {
    style.position = 'absolute';
    style.zIndex = zIndex;
  }

  return (
    <div className="relative w-full h-full" style={style}>
      <picture
          className="h-full w-full"
          style={borderRadius > 0 ? { borderRadius: `${borderRadius}px`, overflow: 'hidden', height: height ? `${height}px` : undefined } : { height: height ? `${height}px` : undefined }}
          key={`${windowDimensions.width}-${windowDimensions.height}`}
      >
        {responsiveImages.srcset ? (
          <source srcSet={responsiveImages.srcset} type="image/webp" />
        ) : (
          <>
            <source srcSet={generateSrcSet(imageUrl, 'webp', `${windowDimensions.width < 768 ? windowDimensions.width : 768}`)} type="image/webp" media="(max-width: 768px)"/>
            <source srcSet={generateSrcSet(imageUrl, 'webp', windowDimensions.width)} type="image/webp" media="(max-width: 1280px)"/>
            <source srcSet={generateSrcSet(imageUrl, 'webp', windowDimensions.width)} type="image/webp" media="(min-width: 1281px)"/>
          </>
        )}
        <img
            ref={imgRef}
            src={responsiveImages.src ? responsiveImages.src : imageUrl}
            srcSet={responsiveImages.srcset}
            sizes={responsiveImages.sizes || '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 100vw'}
            alt={alt}
            fetchPriority={isPriority ? 'high' : 'auto'}
            height={responsiveImages.height || height}
            width={responsiveImages.width || width}
            className={`${combinedClasses} responsive-image`}
            loading={loadingStrategy}
            style={borderRadius > 0 ? { borderRadius: `${borderRadius}px`, height: height ? `${height}px` : undefined } : { height: height ? `${height}px` : undefined }}
        />
      </picture>

      {overlay && overlayOpacity > 0 && (
          <div
              className="absolute inset-0 bg-black"
              style={{
                opacity: overlayOpacity,
                borderRadius: borderRadius > 0 ? `${borderRadius}px` : undefined,
                height: height ? `${height}px` : undefined
              }}
          ></div>
      )}
    </div>
  );
};